import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Loader2, Lightbulb, TrendingUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/PageHeader';
import { getAIInsights, analyzePatientData } from '@/utils/api';
import { toast } from 'sonner';

export default function AIInsights({ patient }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const loadInsights = async () => {
    try {
      const response = await getAIInsights(patient.id);
      setInsights(response.data);
    } catch (error) {
      console.error('Errore nel caricamento insights:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
  }, [patient.id]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await analyzePatientData(patient.id);
      toast.success('Analisi completata! Nuovi insights disponibili.');
      await loadInsights();
    } catch (error) {
      console.error('Errore nell\'analisi:', error);
      toast.error('Errore durante l\'analisi AI');
    } finally {
      setAnalyzing(false);
    }
  };

  const getIconByType = (tipo) => {
    switch (tipo) {
      case 'correlazione':
        return TrendingUp;
      case 'suggerimento':
        return Lightbulb;
      case 'trend':
        return TrendingUp;
      default:
        return Sparkles;
    }
  };

  const getColorByPriority = (priorita) => {
    switch (priorita) {
      case 'alta':
        return 'from-red-500 to-red-600';
      case 'media':
        return 'from-indigo-600 to-indigo-700';
      case 'bassa':
        return 'from-blue-500 to-blue-600';
      default:
        return 'from-indigo-600 to-indigo-700';
    }
  };

  if (loading) {
    return (
      <div className="page-container pb-24">
        <PageHeader title="AI Insights" subtitle="Analisi intelligenti" icon={Sparkles} />
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24">
      <PageHeader title="AI Insights" subtitle="Analisi intelligenti" icon={Sparkles} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-3xl p-8 soft-shadow-lg text-white mb-6"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Genera Nuova Analisi</h2>
            <p className="text-indigo-100 text-base">L'AI analizzerà i tuoi dati recenti per fornirti insights personalizzati</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-8 h-8" />
          </div>
        </div>
        <Button
          data-testid="btn-analyze"
          onClick={handleAnalyze}
          disabled={analyzing}
          className="w-full min-h-[64px] text-lg font-semibold rounded-2xl bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg transition-all duration-300 active:scale-[0.98] disabled:opacity-50"
        >
          {analyzing ? (
            <>
              <Loader2 className="w-6 h-6 mr-2 animate-spin" />
              Analisi in corso...
            </>
          ) : (
            <>
              Avvia Analisi AI
              <Sparkles className="w-6 h-6 ml-2" />
            </>
          )}
        </Button>
      </motion.div>

      <div className="space-y-4">
        {insights.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-3xl p-12 soft-shadow border border-slate-200/60 text-center"
          >
            <Sparkles className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">Nessun insight disponibile</h3>
            <p className="text-slate-600">Avvia un'analisi per generare insights personalizzati</p>
          </motion.div>
        ) : (
          insights.map((insight, idx) => {
            const Icon = getIconByType(insight.tipo);
            const colorClass = getColorByPriority(insight.priorita);

            return (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-white rounded-3xl p-6 sm:p-8 soft-shadow border border-slate-200/60"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClass} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-slate-900">{insight.titolo}</h3>
                      {insight.priorita === 'alta' && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-lg">
                          Alta
                        </span>
                      )}
                    </div>
                    <p className="text-base text-slate-700 leading-relaxed">{insight.contenuto}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="px-3 py-1 bg-slate-100 rounded-lg font-medium capitalize">{insight.tipo}</span>
                  <span>•</span>
                  <span>{new Date(insight.created_at).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
